import React, { useState, useEffect } from 'react';
import './settings.scss';
import { Button, Card, Input, Modal, Form, Spin } from 'antd';
import { fetchData } from '../../../Services/FetchData';
import { API_ENDPOINTS } from '../../../constants/apiEndPoints';
import rightArrow from '../../../assets/images/right_arrow_gray.png'
import { Popup_Toast } from '../../../constants/Popups_Toast';
import { PlaceholderContent } from '../../../constants/Placeholder';
import { toast } from 'react-toastify';

const Settings = () => {
    document.title = "Settings";
    let placeholderContent = PlaceholderContent?.CHANGE_PHONE_NUMBER;

    // Modal states
    const [changePhoneModal, setChangePhoneModal] = useState(false);
    const [logoutModal, setLogoutModal] = useState(false);

    // Form instances
    const [form] = Form.useForm();

    // Phone change states
    const [oldPhone, setOldPhone] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [isNewPhoneInputDisabled, setIsNewPhoneInputDisabled] = useState(false);

    // OTP states
    const [isOtpInputDisabled, setIsOtpInputDisabled] = useState(false);

    // Step management
    const [currentStep, setCurrentStep] = useState(1); // 1: Phone input, 2: OTP verification

    // Loading state
    const [loading, setLoading] = useState(false);

    // Force update state
    const [forceUpdate, setForceUpdate] = useState(0);

    // Constants
    const maxLength = 6;
    let popupContent = Popup_Toast?.LOGOUT_CONFIRMATION;

    // Utility: capitalize first letter of a string (for user-facing messages)
    const capitalizeFirst = (text) => {
        if (typeof text !== 'string') return text;
        const trimmed = text.trim();
        if (!trimmed) return trimmed;
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    };

    // Get current user phone from localStorage
    const getUserPhone = () => {
        const userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
        return userDetails.phone || '';
    };

    // Force form reset when currentStep changes
    useEffect(() => {
        if (currentStep === 2 && newPhone) {
            console.log('Step 2 detected - ensuring OTP field is ready');
            // Reset only the OTP field when moving to step 2
            form.setFieldsValue({ otp: '' });
        }
    }, [currentStep, newPhone, form]);

    const logOut = () => {
        localStorage.setItem('isVerified', false);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userDetails');
        localStorage.removeItem('onBoardingPageCompletion');
        window.location.href = '/login';
    };

    const changePhone = () => {
        const currentPhone = getUserPhone();
        setOldPhone(currentPhone);
        setCurrentStep(1);
        setChangePhoneModal(true);
    };

    const handleCancel = () => {
        form.resetFields();
        setChangePhoneModal(false);
        setCurrentStep(1);
        setOldPhone('');
        setNewPhone('');
        setIsNewPhoneInputDisabled(false);
        setIsOtpInputDisabled(false);
        setForceUpdate(0);
    };

    const handleBackToPhoneInput = () => {
        setCurrentStep(1);
        setIsNewPhoneInputDisabled(false);
        setIsOtpInputDisabled(false);
        setForceUpdate(prev => prev + 1);
        form.resetFields();
        // Set the new_phone field value back to what was entered
        form.setFieldsValue({ new_phone: newPhone });
    };

    const onFinish = async (values) => {
        console.log('Form submitted with values:', values);
        console.log('Current step:', currentStep);

        if (currentStep === 1) {
            // Step 1: Send phone change request
            console.log('Calling handlePhoneChangeRequest');
            await handlePhoneChangeRequest(values);
        } else if (currentStep === 2) {
            // Step 2: Verify OTP
            console.log('Calling handleOtpVerification');
            await handleOtpVerification(values);
        }
    };

    const handlePhoneChangeRequest = async (values) => {
        console.log('handlePhoneChangeRequest called with values:', values);
        try {
            setLoading(true);

            const endpoint = API_ENDPOINTS.UPDATE_PHONE.CHANGE_PHONE;
            const payload = {
                old_phone: oldPhone,
                new_phone: values.new_phone
            };

            const response = await fetchData(endpoint, payload);
            console.log('Phone change response:', response);
            console.log('Response type:', typeof response);
            console.log('Response success:', response?.success);
            console.log('Response success type:', typeof response?.success);
            console.log('Response structure:', JSON.stringify(response, null, 2));

            setLoading(false);

            // Check if response is successful - handle different response structures
            // Response might be nested or have different success indicators
            const isSuccess = response?.success === true ||
                response?.success === 'true' ||
                response?.success === 1 ||
                (response?.data?.success === true) ||
                (response?.otp !== undefined);

            console.log('Is success:', isSuccess);

            if (isSuccess) {
                console.log('Success - proceeding to OTP step');
                console.log('Setting currentStep to 2');

                // Show success message first
                const successMessage = response?.message || response?.data?.message || 'OTP sent successfully to your new phone number';
                toast.success(successMessage, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 3000,
                    className: 'custom_toast'
                });

                // Use setTimeout to ensure state updates happen after the current execution context
                setTimeout(() => {
                    // Update all states to show OTP step
                    setNewPhone(values.new_phone);
                    setIsNewPhoneInputDisabled(true);
                    setForceUpdate(prev => prev + 1);
                    setCurrentStep(2); // Set this last to trigger re-render with all other states updated

                    console.log('States updated - newPhone:', values.new_phone, 'currentStep set to 2');
                }, 0);
            } else {
                // Show the actual error message from API
                console.log('Phone change failed:', response?.message);
                const errorMessage = response?.message || response?.data?.message || 'Failed to send OTP. Please try again.';
                toast.error(errorMessage, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 3000,
                    className: 'custom_toast'
                });
            }
        } catch (error) {
            setLoading(false);
            console.error('Error changing phone:', error);

            // Extract error message from backend response
            // Error can be a string (from axios interceptor) or an object
            let errorMessage = 'An error occurred while changing the phone number. Please try again.';

            if (typeof error === 'string') {
                // Error is already a string message from axios interceptor
                errorMessage = error;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            toast.error(capitalizeFirst(errorMessage), {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000,
                className: 'custom_toast'
            });
        }
    };

    const handleOtpVerification = async (values) => {
        try {
            setLoading(true);

            const endpoint = API_ENDPOINTS.UPDATE_PHONE.VERIFY_PHONE_CHANGE;
            const payload = {
                new_phone: newPhone,
                otp: values.otp
            };

            const response = await fetchData(endpoint, payload);
            console.log('OTP verification response:', response);

            if (response?.message === 'Phone number changed successfully') {
                setLoading(false);

                // Update user details in localStorage
                const userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
                userDetails.phone = newPhone;
                localStorage.setItem('userDetails', JSON.stringify(userDetails));

                // Close modal and reset immediately so UI updates before toast
                setChangePhoneModal(false);
                handleCancel(); // Reset all states

                // Show success toast after closing the modal
                toast.success(response?.message || 'Phone number changed successfully!', {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 3000,
                    className: 'custom_toast'
                });
            } else {
                setLoading(false);
                const formattedMessage = response?.message ? capitalizeFirst(response.message) : 'Invalid OTP. Please try again.';
                toast.error(formattedMessage, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 3000,
                    className: 'custom_toast'
                });
            }
        } catch (error) {
            setLoading(false);
            console.error('Error verifying OTP:', error);

            // Extract error message from backend response
            // Error can be a string (from axios interceptor) or an object
            let errorMessage = 'An error occurred while verifying OTP. Please try again.';

            if (typeof error === 'string') {
                // Error is already a string message from axios interceptor
                errorMessage = error;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000,
                className: 'custom_toast'
            });
        }
    };

    const validateOTP = (_, value) => {
        if (value && value.length === maxLength) {
            return Promise.resolve();
        } else {
            return Promise.reject('Please enter a valid 6-digit OTP.');
        }
    };

    const onChangeOtp = (e) => {
        const inputValue = e.target.value;
        setIsOtpInputDisabled(inputValue.length !== maxLength);
    };

    const handleNewPhoneChange = (e) => {
        // No need for button state management as we use form validation
    };

    const getModalTitle = () => {
        return currentStep === 1 ? 'Change Mobile Number' : 'Verify New Mobile Number';
    };

    const getModalContent = () => {
        console.log('getModalContent called with currentStep:', currentStep);
        if (currentStep === 1) {
            return (
                <>
                    <p className='md'>Enter your new mobile number below. An OTP will be sent to verify this number.</p>

                    {/* Current Phone Display */}
                    <div className="current_phone_display" style={{ marginBottom: '1em', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <p className='text_sm mb-0'><strong>Current Phone:</strong> +91 {oldPhone}</p>
                    </div>

                    <Form.Item
                        className='change_phone mb-0'
                        name="new_phone"
                        rules={[
                            { required: true, message: 'Please enter the new phone number!' },
                            { len: 10, message: 'Phone number must be exactly 10 digits!' }
                        ]}
                    >
                        <div className="wrap">
                            <div className="input_field input_wrap" style={{ width: 'auto' }}>
                                <span className='plus_symbol'>+</span>
                                <input
                                    placeholder="Enter country code"
                                    type="text"
                                    value="91"
                                    disabled={true}
                                    className='country_code  bg_transparent'
                                    maxLength={2}
                                    onKeyDown={(e) => {
                                        if (!(e.key === 'Backspace' || e.key === 'Delete' || /^\d$/.test(e.key))) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </div>
                            <Input
                                classNames={'demo_field'}
                                placeholder={placeholderContent?.ChangePhoneNumber || "Enter new phone number"}
                                type="text"
                                onChange={handleNewPhoneChange}
                                className='input_field'
                                maxLength={10}
                                disabled={isNewPhoneInputDisabled}
                                onKeyDown={(e) => {
                                    if (!(e.key === 'Backspace' || e.key === 'Delete' || /^\d$/.test(e.key))) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </div>
                    </Form.Item>
                </>
            );
        } else {
            console.log('Rendering OTP step with newPhone:', newPhone);
            return (
                <>
                    <p className='md'>Please enter the 6 digit OTP that we sent to +91 {newPhone}</p>

                    {/* Show the entered phone number for reference */}
                    <div className="phone_display" style={{ marginBottom: '1em', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #e6f3ff' }}>
                        <p className='text_sm mb-0'><strong>New Phone:</strong> +91 {newPhone}</p>
                    </div>

                    <div style={{ maxWidth: '509px', margin: 'auto', paddingTop: '1em', paddingBottom: '1em' }}>
                        <Form.Item
                            name='otp'
                            className='mb-0'
                            rules={[
                                { validator: validateOTP }
                            ]}
                        >
                            <Input
                                className='input_field'
                                maxLength={6}
                                onChange={onChangeOtp}
                                style={{
                                    margin: '10px auto',
                                    color: '#9E9E9E',
                                    textAlign: 'center',
                                    boxShadow: 'none',
                                    outline: 'none',
                                    height: 'calc(1vw + 38px)'
                                }}
                                type='text'
                                placeholder='- - - - - -'
                                onKeyDown={(e) => {
                                    if (!(e.key === 'Backspace' || e.key === 'Delete' || /^\d$/.test(e.key))) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </div>
                </>
            );
        }
    };

    const getModalFooter = () => {
        if (currentStep === 1) {
            return (
                <div className='text-center button_wrapper'>
                    <Button key="close" type="text" className="short custom_btn" onClick={handleCancel}>
                        Close
                    </Button>
                    <Button
                        className='md wide'
                        key="submit"
                        type="primary"
                        htmlType="submit"
                        form="changePhoneForm"
                    >
                        Continue
                    </Button>
                </div>
            );
        } else {
            return (
                <div className='text_center flex_center change_number_footer'>
                    <Button key="back" type="text" className="short custom_btn" onClick={handleBackToPhoneInput}>
                        Back
                    </Button>
                    <Button key="close" type="text" className="short custom_btn" onClick={handleCancel}>
                        Close
                    </Button>
                    <Button
                        type='primary'
                        className='md wide'
                        htmlType="submit"
                        form="changePhoneForm"
                        disabled={isOtpInputDisabled}
                    >
                        Verify
                    </Button>
                </div>
            );
        }
    };

    return (
        <>
            {
                loading && (
                    <div className="spinner center">
                        <Spin />
                    </div>
                )
            }
            <div className="settings_wrap">
                <Card className='listing '>
                    <Button type="link" className='dflt_btn_removed text_dark ' onClick={changePhone}>
                        <div className="flex_wrap">
                            <div className="left">
                                <h4 className='text_red text_md'>Change Mobile Number</h4>
                                <p className='mb-0 text_sm'>Update your contact information by changing your phone number.</p>
                            </div>
                            <figure className="right">
                                <img src={rightArrow} alt="" />
                            </figure>
                        </div>
                    </Button>
                </Card>
                <Card style={{ marginTop: "24px" }}>
                    <Button type="link" className='dflt_btn_removed text_dark' onClick={() => {
                        setLogoutModal(true)
                    }}>
                        <div className="flex_wrap">
                            <div className="left">
                                <h4 className='text_red text_md'>{popupContent?.content?.title}</h4>
                                <p className='mb-0 text_sm'>Log out and return to the login page.</p>
                            </div>
                            <figure className="right">
                                <img src={rightArrow} alt="" />
                            </figure>
                        </div>
                    </Button>
                </Card>
            </div>

            {/* Logout Modal */}
            <Modal afterClose={() => (document.body.style.overflow = 'initial')} className='sm_modal work_modal center_modal logout_modal' open={logoutModal} footer={[
                <div className='text-center'>
                    <Button key="submit" type="text" className='short  custom_btn' onClick={() => {
                        setLogoutModal(false)
                    }}>
                        Close
                    </Button>
                    <Button key="submit" type="primary" className='short wide ' onClick={() => {
                        logOut()
                    }}>
                        Continue
                    </Button>
                </div>
            ]} onCancel={() => {
                setLogoutModal(false)
            }}>
                <div className="modal_cnt">
                    <h2 className="text_lg text_red">Log Out</h2>
                    <p className="md">
                        {popupContent?.content?.description}
                    </p>
                </div>
            </Modal>

            {/* Combined Phone Change Modal */}
            <Modal
                key={`phone-modal-${currentStep}-${forceUpdate}`}
                className='sm_modal change_phone_modal center_modal'
                open={changePhoneModal}
                onCancel={handleCancel}
                footer={getModalFooter()}
                destroyOnClose={false}
                forceRender={true}
            >
                <Form
                    id="changePhoneForm"
                    form={form}
                    onFinish={onFinish}
                >
                    <div className="wrap">
                        <h2 className='text_lg text_red text-center' style={{ marginBottom: '.7em' }}>
                            {getModalTitle()}
                        </h2>
                        {getModalContent()}
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default Settings;